import { Image } from '@rocco/components/image';
import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { OVERLAY_CAP } from '~/ui-rocco/pages/detail-shared';

interface EditorialDirectoryHeroProps {
    title: string;
    /** Publication and date, e.g. "Curated Collection, 19 June 2026". */
    meta?: string;
    /** The article's intro text, set opposite the headline. */
    standfirst?: string;
    link: string;
    /** Overrides the default control label, for a curated placement. */
    ctaLabel?: string;
    imageSrc?: string;
    className?: string;
}

/**
 * The /articles masthead: a full-bleed image carrying the promoted article's
 * headline, its publication line, a standfirst and a Read Article control.
 *
 * Laid out against the Figma frame (60:1949, 1920x1372.79) with children
 * positioned as percentages of it, so the composition holds its proportions at
 * any width rather than reflowing. Type is rem, which is 16px at 1920 and
 * scales with the viewport, so the Figma pixel values carry over directly.
 */
export const EditorialDirectoryHero = ({
    title,
    meta,
    standfirst,
    link,
    ctaLabel = 'Read Article',
    imageSrc,
    className,
}: EditorialDirectoryHeroProps) => {
    return (
        <section
            aria-labelledby="editorial-directory-title"
            className={cn(
                // Breaks out of the layout's 1920 .ui-container so the media
                // runs to the viewport edges on wide screens, as the project
                // and professional detail heroes do. The overlay inside stays
                // anchored to the centred container via OVERLAY_CAP.
                'relative overflow-hidden bg-[#131313]',
                'ml-[calc(50%-50vw)] w-screen',
                'aspect-[1920/1372.79] max-md:aspect-[414/560]',
                className,
            )}
        >
            {imageSrc && (
                <Image
                    src={imageSrc}
                    alt=""
                    fill={true}
                    fit="cover"
                    loading="eager"
                    className="absolute inset-0"
                />
            )}

            {/*
             * Legibility wash. The design's scrim alone clears its own hero
             * photo, which is dark exactly where the headline sits; real banner
             * images are arbitrary and the headline washes out over a bright
             * one. A flat tint under the scrim lifts contrast everywhere
             * without altering the gradient the design draws.
             */}
            <div
                aria-hidden={true}
                className="pointer-events-none absolute inset-0 bg-[#131313]/[0.22]"
            />

            <div
                aria-hidden={true}
                /*
                 * Figma draws a 590px scrim, which clears its own hero photo
                 * because that photo is dark where the headline sits. Real
                 * article images are arbitrary, so the scrim is raised to 720px
                 * to start above the headline and keep it legible on a bright
                 * one. Same colour ramp, more of it.
                 */
                className="pointer-events-none absolute inset-x-0 bottom-0 h-[36.875rem] bg-gradient-to-t from-[#131313]/[0.89] from-[6.207%] to-transparent to-[70.169%]"
            />

            {/*
             * Desktop: the Figma composition, positioned against the frame.
             * OVERLAY_CAP anchors the offsets to the centred 1920 container so
             * the media stays edge-to-edge on wider screens while the headline
             * stops travelling outwards, matching the detail-page heroes.
             */}
            <div className={cn(OVERLAY_CAP, 'max-md:hidden')}>
                {/*
                 * Anchored by its baseline rather than its top: Figma's box fits
                 * the two lines its mock title happens to take, and clamping to
                 * that crops real headlines. Growing upward instead keeps the
                 * last line where the design puts it, clear of the publication
                 * line, however long the headline runs.
                 */}
                <h1
                    id="editorial-directory-title"
                    className="absolute m-0 font-gestura text-[5.625rem] leading-[1.1] font-normal tracking-[-0.225rem] text-[#e5e5e5]"
                    style={{
                        left: '2.9604%',
                        bottom: '31.31%',
                        width: '47.666%',
                    }}
                >
                    {title}
                </h1>

                {meta && (
                    <p
                        className="absolute m-0 font-sans text-[1.375rem] leading-[1.25] font-medium tracking-[-0.0344rem] text-white"
                        style={{ left: '3.9448%', top: '71.834%' }}
                    >
                        {meta}
                    </p>
                )}

                {/*
                 * Clamped to four lines and anchored by its own bottom edge:
                 * the intro text an article carries is open-ended, and past
                 * four lines it climbs into the headline above.
                 */}
                {standfirst && (
                    <p
                        className="absolute m-0 line-clamp-4 -translate-y-full font-gestura text-[2.2469rem] leading-[0.9] font-normal tracking-[-0.0674rem] text-white"
                        style={{
                            left: '58.866%',
                            top: '83.862%',
                            width: '37.23%',
                        }}
                    >
                        {standfirst}
                    </p>
                )}

                <RoccoLink
                    to={link}
                    className="absolute flex h-[3.25rem] items-center justify-center rounded-full border border-solid border-white px-[1.723rem] font-sans text-[1rem] leading-[1.3] font-medium text-[#f3f3f3] shadow-[0_0.2027rem_1.4189rem_rgba(0,0,0,0.11)] transition-colors hover:bg-white/10"
                    style={{ left: '58.463%', top: '87.996%' }}
                >
                    {ctaLabel}
                </RoccoLink>
            </div>

            {/* Mobile: the same content, stacked and flowed. */}
            <div className="absolute inset-x-0 bottom-0 flex flex-col gap-[1rem] px-[1.5rem] pb-[2rem] md:hidden">
                <h1 className="m-0 line-clamp-4 font-gestura text-[2.5rem] leading-[2.75rem] font-normal tracking-[-0.1rem] text-[#e5e5e5]">
                    {title}
                </h1>
                {meta && (
                    <p className="m-0 font-sans text-[0.875rem] leading-[1.25] font-medium text-white">
                        {meta}
                    </p>
                )}
                <RoccoLink
                    to={link}
                    className="mt-[0.5rem] flex h-[2.75rem] w-fit items-center justify-center rounded-full border border-solid border-white px-[1.5rem] font-sans text-[0.875rem] font-medium text-[#f3f3f3]"
                >
                    {ctaLabel}
                </RoccoLink>
            </div>
        </section>
    );
};
